import { coQuyenModuleDaCai, toanQuyen } from '../../hooks/usePermission';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Blocks,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  FolderOpen,
  Search,
  SearchX,
} from 'lucide-react';

import { cn } from '../../lib/cn';
import { useT } from '../../i18n';
import { NHAN_CHI_TIET, NHAN_PHIEN_BAN } from '../../lib/phien-ban';
import { useAuthStore } from '../../stores/auth';
import { useUiStore } from '../../stores/ui';
import { MenuTaiKhoan } from './MenuTaiKhoan';
import { useNhanDien } from '../../modules/ho-so-to-chuc/hooks/useNhanDien';
import { useDuocXemLicenseCucBo } from '../../modules/license-cuc-bo/hooks/useLicenseCucBo';
import { TheLicenseThanhBen } from '../../modules/license-cuc-bo/components/TheLicenseThanhBen';
import { useModuleDaCai } from '../../modules/module-da-cai/hooks/useModuleDaCai';
import { laNhomCon, nhomMenuModule, type MucMenuModule } from '../../modules/module-da-cai/nghiep-vu/menu';
import { HIDDEN_HREFS, NAV_SECTIONS, SECTION_TONE } from './nav-config';
import type { NavItem, NavSection } from './nav-config';
import { SidebarLeafItem, SidebarParentItem } from './SidebarItem';
import { SidebarFlyout } from './SidebarFlyout';
import { SidebarSearch, useMoOTim } from './SidebarSearch';
import { ganLa, locCayDieuHuong, tachTuKhoa, tenNhom } from './nav-search';
import { useNavPrefs } from '../../stores/nav-prefs';
import { MODULES, duocVao } from '../../app/modules';
import { usePhamVi } from '../../hooks/usePhamVi';
import { TIEN_TO, ghep, tach } from '../../lib/duong-dan-cong';
import { moduleForPath } from '../../lib/route-permissions';
import type { PhamViCong } from '../../lib/pham-vi-cong';

/** Có mục con nào là route đang xem không. */
function isChildActive(item: NavItem, pathname: string): boolean {
  // Cùng luật với mục lá: mục con `matchPrefix` (`Thiết lập Chứng từ OCR`) giữ cụm cha mở ở trang chi tiết.
  return (item.children ?? []).some((child) => laRouteDangXem(child, pathname));
}

/** Nhóm này có chứa route đang xem hay không. */
function nhomChuaRoute(section: NavSection, pathname: string): boolean {
  return section.items.some(
    (item) =>
      laRouteDangXem(item, pathname) ||
      (item.children ?? []).some((c) => laRouteDangXem(c, pathname)),
  );
}

/**
 * Khoá lưu trạng thái gập của một cụm con.
 *
 * Phải gắn tên nhóm vào, **không** dùng khoá mục trần: `nav-config.ts` đã có
 * nhãn trùng giữa các nhóm (`Tổng quan` ở 5 nhóm, `Nguồn dữ liệu` ở 2). Hôm nay
 * chưa có hai *mục cha* trùng nhãn, nhưng ADR-UI-0009 hứa "thêm nhãn điều hướng
 * mới thì không phải khai gì thêm" — nên đây là cái bẫy chờ sẵn, không phải
 * chuyện lý thuyết. Dùng khoá trần thì gập một cụm sẽ gập luôn cụm trùng tên ở
 * nhóm khác, và trạng thái đó còn được lưu lại.
 *
 * Ghép từ **khoá dịch**, không phải nhãn đã dịch: nhãn đổi theo ngôn ngữ, nên
 * khoá lưu cũng đổi theo và người dùng mất hết trạng thái gập mỗi lần chuyển
 * ngôn ngữ — `nav-prefs` trong `localStorage` sẽ đầy khoá rác của ngôn ngữ cũ.
 */
function khoaCha(section: NavSection, item: NavItem): string {
  return `${section.khoaNhom}/${idMuc(item)}`;
}

/**
 * Định danh một mục để làm `key` và khoá gập. Mục sinh lúc chạy (nhóm con của module, ADR-0121) cùng mang
 * khoá dịch dự phòng `nav.muc.moduleDaCai`, nên phải phân biệt bằng nhãn nguyên văn; nếu không hai nhóm con
 * cùng module sẽ trùng `key` và gập chung một trạng thái.
 */
function idMuc(item: NavItem): string {
  return item.nhanThang ?? item.khoa;
}

/**
 * Mục lá này có phải route đang xem — cùng luật với `NavLink` trong `SidebarItem`.
 *
 * `matchPrefix` khớp cả route con (`Nhận dạng OCR` sáng cho
 * `/xu-ly/nhan-dang/:schemaCode`). Mục nhận `href` **đã ghép tiền tố cổng**,
 * nên `Dashboard` ở đây là `/client`, không phải `/`.
 *
 * Gốc nhánh (`/client`) vẫn phải khớp **chính xác**: khớp tiền tố thì mọi
 * đường dẫn dưới nhánh đều bắt đầu bằng nó và nhóm nào cũng tự mở.
 */
function laRouteDangXem(item: NavItem, pathname: string): boolean {
  if (!item.href) return false;
  if (item.matchPrefix && item.href !== '/') return pathname.startsWith(item.href);
  return pathname === item.href;
}

export function Sidebar() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const user = useAuthStore((s) => s.user);
  const { pathname } = useLocation();
  const navigate = useNavigate();


  /** Cổng của người đang đăng nhập: nền tảng hay tenant (`lib/pham-vi-cong`). */
  const phamVi = usePhamVi();

  /**
   * Nhân sự nền tảng đang xem giao diện tenant hay không.
   *
   * Suy từ **URL hiện tại**, không phải một cờ lưu riêng: URL đã là nguồn sự
   * thật của việc "đang ở đâu", nên thêm một cờ song song là tạo ra hai nguồn
   * có thể lệch nhau — mở `/client/tri-thuc` từ dấu trang sẽ ra một thanh điều
   * hướng không có mục nào đang sáng.
   */
  const dangXemTenant = phamVi === 'nen-tang' && tach(pathname).phamVi === 'tenant';

  /** Kiểm quyền reactive, dùng chung luật với guard route. */
  const coQuyen = useCallback(
    (ma: string): boolean => {
      if (!user) return false;
      if (toanQuyen(user)) return true;
      return user.permissions.includes(ma);
    },
    [user],
  );

  const { t } = useT();
  const nhanDien = useNhanDien();

  /** Mục `chiOnPremise` (màn license cục bộ, ADR-0058 mục 8): bản on-premise, quản trị viên bản cài. */
  const hienLicenseCucBo = useDuocXemLicenseCucBo();

  /*
   * Module đã cài của tenant, sinh nhóm nav lúc chạy (ADR-0096). Chỉ bật ở tài khoản tenant: endpoint cần
   * `tenant_id` mà nhân sự nền tảng không có. Lỗi hay đang tải thì không có nhóm, phần còn lại vẫn dùng được.
   */
  const { data: moduleDaCai } = useModuleDaCai(phamVi === 'tenant');

  /**
   * Nhãn của một mục: `nhanThang` thắng, rồi mới tra catalog.
   *
   * Mục sinh từ dữ liệu lúc chạy (bot) mang nhãn nguyên văn; mọi mục khác mang
   * khoá. Gom vào một hàm để không chỗ nào quên nhánh `nhanThang` và hiện ra
   * chuỗi `bot.<id>`.
   */
  const nhanMuc = useCallback(
    (item: NavItem): string => item.nhanThang ?? t(item.khoa),
    [t],
  );

  // Nhóm/cụm nào đang đóng: ở kho riêng để **nhớ qua lần tải sau** (ADR-UI-0009).
  const nhomDong = useNavPrefs((s) => s.nhomDong);
  const nhomMo = useNavPrefs((s) => s.nhomMo);
  const chaDong = useNavPrefs((s) => s.chaDong);
  const datNhom = useNavPrefs((s) => s.datNhom);
  const datCha = useNavPrefs((s) => s.datCha);
  const datTatCa = useNavPrefs((s) => s.datTatCa);

  // Từ khoá tìm kiếm. **Không** lưu lại: mở app mà cây đang bị lọc bởi chữ gõ
  // hôm qua thì trông như hỏng.
  const [tuKhoa, setTuKhoa] = useState('');
  const [viTriTro, setViTriTro] = useState(-1);
  const [canTieuDiem, setCanTieuDiem] = useState(false);
  const oTim = useRef<HTMLInputElement>(null);
  /** Phần tử của mục đang trỏ, để cuộn nó vào vùng thấy được. */
  const mucDangTro = useRef<HTMLAnchorElement>(null);

  /** `/` hoặc `⌘K` nhảy vào ô tìm. Thanh đang gập thì mở ra trước. */
  const moOTim = useCallback(() => {
    if (collapsed) {
      toggleSidebar();
      // Lúc này ô tìm **chưa có trong DOM** (thanh gập thì chỗ đó là nút kính
      // lúp), nên `oTim.current` còn null — gọi focus ngay là mất tiêu điểm
      // không dấu vết. Đặt cờ để `useEffect` lấy tiêu điểm sau khi React đã
      // vẽ ô tìm ra.
      setCanTieuDiem(true);
      return;
    }
    oTim.current?.focus();
    oTim.current?.select();
  }, [collapsed, toggleSidebar]);

  useMoOTim(moOTim);

  /*
   * Lấy tiêu điểm sau khi thanh đã mở ra và ô tìm đã có trong DOM.
   * `useEffect` chạy sau khi React gắn ref, nên đây là thời điểm sớm nhất mà
   * `oTim.current` chắc chắn khác null.
   */
  useEffect(() => {
    if (!canTieuDiem || collapsed) return;
    setCanTieuDiem(false);
    oTim.current?.focus();
    oTim.current?.select();
  }, [canTieuDiem, collapsed]);

  /*
   * Gập thanh thì **xoá từ khoá**.
   *
   * Ô tìm chỉ tồn tại khi thanh mở rộng. Giữ từ khoá lúc gập thì rail 76px vẫn
   * bị lọc — chỉ còn vài biểu tượng trong số ~59 — mà thứ giải thích vì sao thì
   * đã biến mất cùng ô tìm. Từ khoá không khớp gì thì còn tệ hơn: trạng thái
   * rỗng bị nhồi vào cột 76px. Người dùng không có cách nào thấy hay xoá bộ
   * lọc đang chạy.
   *
   * Xoá luôn `canTieuDiem`: bấm `/` lúc đang gập rồi gập lại ngay thì cờ còn
   * treo, và lần mở thanh sau đó (vì việc khác hẳn) sẽ bị giật tiêu điểm vào ô
   * tìm mà người dùng không yêu cầu.
   */
  useEffect(() => {
    if (!collapsed) return;
    setTuKhoa('');
    setViTriTro(-1);
    setCanTieuDiem(false);
  }, [collapsed]);

  /**
   * Cây điều hướng sau khi lọc theo cổng, dịch vụ và mục bị ẩn.
   *
   * Không còn chèn bot vào thanh: theo native, "Trò chuyện với bot" là một màn lưới
   * (`/tro-ly-ai/tro-chuyen`, ADR-0086) — danh sách bot dùng được do module trả lúc
   * mở màn, không phải mọi trang đều đi hỏi.
   */
  const sections = useMemo<NavSection[]>(() => {
    /*
     * Thanh điều hướng hiện nhóm của **đúng một cổng** — cổng đang xem, chứ
     * không phải cổng của tài khoản.
     *
     * Hai thứ khác nhau với nhân sự nền tảng đang bật chế độ xem giao diện
     * tenant: họ *thuộc* cổng nền tảng nhưng đang *ở* nhánh `/client`. Trộn
     * cả hai bộ nhóm vào một thanh là nói sai họ đang ở đâu, và `QUẢN TRỊ NỀN
     * TẢNG` nằm trên 11 nhóm nghiệp vụ trông như tenant cũng quản trị được
     * nền tảng. Lối chuyển giữa hai cổng nằm ở menu tài khoản.
     */
    const congDangXem: PhamViCong = dangXemTenant ? 'tenant' : phamVi;

    /*
     * Nhóm quản trị nền tảng **sinh từ registry** (`app/modules.ts`), lọc theo
     * đúng luật quyền mà guard route dùng — một luật, hai nơi đọc, nên thanh
     * điều hướng không bao giờ chỉ tới màn người dùng không vào được.
     *
     * Tài khoản tenant không bao giờ sinh ra mục nào ở đây, kể cả khi bộ quyền
     * của họ tình cờ có `TENANT.READ`.
     */
    const mucQuanTri: NavItem[] =
      congDangXem === 'nen-tang'
        ? MODULES.filter((m) => duocVao(m, coQuyen, 'nen-tang')).map((m) => ({
            khoa: m.khoaNhan,
            href: `${TIEN_TO['nen-tang']}/${m.id}`,
            icon: m.icon,
          }))
        : [];

    const nhomQuanTri: NavSection[] =
      mucQuanTri.length > 0
        ? [
            {
              khoaNhom: 'nav.nhom.quanTriNenTang' as const,
              phamVi: 'nen-tang' as const,
              items: mucQuanTri,
            },
          ]
        : [];

    /*
     * Module đã cài của cổng tenant (ADR-0096): mỗi module ACTIVE có giao diện là **một nhóm** ngang các
     * nhóm của cổng, mỗi mục `menus` người này có quyền là một mục, mở màn nạp remote Module Federation.
     * Chỉ cho tài khoản tenant thật, cùng điều kiện bật `useModuleDaCai` ở trên.
     */
    const nhomModuleDaCai: NavSection[] =
      congDangXem === 'tenant' && phamVi === 'tenant'
        ? nhomMenuModule(moduleDaCai ?? [], (ma) => coQuyenModuleDaCai(user, ma)).map((nhom) => ({
            khoaNhom: `module:${nhom.code}` as const,
            nhanNhomThang: nhom.ten,
            phamVi: 'tenant' as const,
            items: nhom.muc.map((phanTu): NavItem => {
              const laMuc = (muc: MucMenuModule): NavItem => ({
                khoa: 'nav.muc.moduleDaCai' as const, // dự phòng — nhanThang thắng, xem nhanMuc().
                nhanThang: muc.nhan,
                href: muc.href,
                icon: Blocks,
                // Mục gốc của module không sáng theo mọi trang con; mục khác sáng cả trang chi tiết bên dưới nó.
                matchPrefix: !muc.laGoc,
              });
              // Nhóm con (ADR-0121): mục cha chỉ gập/mở, tên do bên nộp gói đặt nên không dịch.
              if (laNhomCon(phanTu)) {
                return {
                  khoa: 'nav.muc.moduleDaCai' as const,
                  nhanThang: phanTu.ten,
                  icon: FolderOpen,
                  children: phanTu.muc.map(laMuc),
                };
              }
              return laMuc(phanTu);
            }),
          }))
        : [];

    /*
     * Nhóm nghiệp vụ: chỉ khi đang xem nhánh `/client`, và chỉ nhóm thuộc dịch
     * vụ người này được dùng (ADR-0034). `dichVu` để trống — tài khoản mẫu,
     * nhân sự nền tảng đang xem giao diện tenant — thì không lọc theo gói.
     */
    const dichVuDuocDung = user?.dichVu;
    const nhomNghiepVu =
      congDangXem === 'tenant'
        ? NAV_SECTIONS.filter(
            (section) =>
              section.phamVi === 'tenant' &&
              (!section.dichVu || !dichVuDuocDung || dichVuDuocDung.includes(section.dichVu)),
          )
        : [];

    /*
     * Mục mà `PermissionGuard` sẽ chặn thì ẩn, không để người dùng bấm vào rồi gặp 403 (ADR-UI-0005). Chỉ xét
     * tài khoản tenant thật (`dichVu` có mặt): quyền của họ tính từ gói nên thiếu phân hệ là thiếu thật — hôm nay
     * chỉ rơi vào màn cấu hình của dịch vụ, vốn chỉ quản trị tổ chức được dùng (ADR-UI-0015). Tài khoản mẫu và
     * nhân sự nền tảng đang xem chéo giữ nguyên cây như trước.
     */
    const moDuoc = (href: string | undefined): boolean => {
      if (!href || !user || !dichVuDuocDung || toanQuyen(user)) return true;
      const ma = moduleForPath(href);
      return !ma || user.permissions.some((p) => p.startsWith(`${ma}.`));
    };
    /** Mục khai `quyen` (màn của nền tảng, ADR-0104): cùng phạm vi áp như `moDuoc`. */
    const duQuyen = (item: NavItem): boolean =>
      !item.quyen || !user || !dichVuDuocDung || item.quyen.some((ma) => coQuyenModuleDaCai(user, ma));

    return [...nhomQuanTri, ...nhomNghiepVu, ...nhomModuleDaCai].map((section) => ({
      khoaNhom: section.khoaNhom,
      nhanNhomThang: section.nhanNhomThang,
      phamVi: section.phamVi,
      items: section.items.flatMap<NavItem>((item) => {
        /*
         * `nav-config.ts` khai đường dẫn **gốc**; tiền tố cổng ghép ở đây.
         * `HIDDEN_HREFS` đối chiếu trước khi ghép vì nó cũng khai đường dẫn
         * gốc — đối chiếu sau khi ghép thì không khớp gì và mục ẩn hiện lại.
         */
        const voiTienTo = (m: NavItem): NavItem =>
          m.href ? { ...m, href: ghep(m.href, section.phamVi) } : m;

        if (item.children) {
          const kept = item.children.filter(
            (child) =>
              (!child.href || !HIDDEN_HREFS.includes(child.href)) &&
              (!child.chiOnPremise || hienLicenseCucBo) &&
              moDuoc(child.href),
          );
          if (kept.length === 0) return [];
          return [{ ...item, children: kept.map(voiTienTo) }];
        }

        if (item.href && HIDDEN_HREFS.includes(item.href)) return [];
        if (item.chiOnPremise && !hienLicenseCucBo) return [];
        if (!moDuoc(item.href) || !duQuyen(item)) return [];
        return [voiTienTo(item)];
      }),
      // Mục rỗng sau khi lọc thì bỏ hẳn.
    })).filter((section) => section.items.length > 0);
  }, [coQuyen, phamVi, dangXemTenant, user, hienLicenseCucBo, moduleDaCai]);

  /* ------------------------------------------------------------------ *
   * Tìm kiếm: lọc cây, rồi điều hướng kết quả bằng bàn phím.
   * ------------------------------------------------------------------ */

  const tuDaTach = useMemo(() => tachTuKhoa(tuKhoa), [tuKhoa]);
  const dangTim = tuDaTach.length > 0;

  /** Cây sau khi lọc. Không gõ gì thì đúng là cây gốc, không tốn công lọc. */
  const sectionsHienRa = useMemo(
    () => (dangTim ? locCayDieuHuong(sections, tuKhoa, t) : sections),
    [dangTim, sections, tuKhoa, t],
  );

  /** Các mục lá còn lại, theo thứ tự thấy trên màn — ↑ ↓ đi theo thứ tự này. */
  const laHienRa = useMemo(
    () => (dangTim ? ganLa(sectionsHienRa) : []),
    [dangTim, sectionsHienRa],
  );

  /*
   * Con trỏ phải nằm trong danh sách hiện tại. Gõ thêm một ký tự làm danh sách
   * ngắn đi thì con trỏ cũ có thể trỏ ra ngoài mảng, và Enter sẽ mở sai màn
   * (hoặc ném). Kẹp lại ở đây thay vì `useEffect` để không có một nhịp vẽ nào
   * mang chỉ số sai.
   */
  const troHieuLuc = viTriTro >= 0 && viTriTro < laHienRa.length ? viTriTro : -1;

  /*
   * Cuộn mục đang trỏ vào vùng thấy được.
   *
   * Cây có ~59 mục trong một vùng `overflow-y-auto` cao bằng màn hình, nên ↑ ↓
   * đi quá fold là chuyện thường — và `ArrowUp` từ mục đầu vòng xuống mục
   * **cuối**, gần như luôn nằm ngoài tầm mắt. Không cuộn theo thì người dùng
   * thấy con trỏ biến mất, bấm tiếp, rồi Enter mở một màn họ chưa từng thấy.
   *
   * `block: 'nearest'` chỉ cuộn khi thật sự cần, nên mục đã thấy rồi thì danh
   * sách không nhảy dưới tay người dùng.
   */
  useEffect(() => {
    if (troHieuLuc < 0) return;
    mucDangTro.current?.scrollIntoView({ block: 'nearest' });
  }, [troHieuLuc]);

  /** Mục lá đang được ↑ ↓ trỏ tới. */
  const laDangTro = useCallback(
    (item: NavItem): boolean =>
      dangTim && troHieuLuc >= 0 && laHienRa[troHieuLuc] === item,
    [dangTim, laHienRa, troHieuLuc],
  );

  /**
   * Nhãn nào xuất hiện nhiều hơn một lần trong kết quả.
   *
   * Chỉ những nhãn này mới cần thêm tên nhóm bên dưới. Thêm cho **mọi** dòng
   * thì thanh điều hướng thành hai dòng một mục và cao gấp đôi, đổi một vấn đề
   * này lấy đúng vấn đề ta đang chữa.
   */
  const nhanTrung = useMemo(() => {
    if (!dangTim) return new Set<string>();
    const dem = new Map<string, number>();
    // Đếm trên nhãn **đã dịch**, không trên khoá: hai khoá khác nhau có thể cho
    // ra cùng một chữ ở một ngôn ngữ (`nav.muc.nguonDuLieu` và
    // `nav.muc.nguonBaoCao` đều là `Data sources`), và đúng lúc đó người dùng
    // mới cần tên nhóm để phân biệt.
    for (const la of laHienRa) {
      const nhan = nhanMuc(la);
      dem.set(nhan, (dem.get(nhan) ?? 0) + 1);
    }
    return new Set([...dem].filter(([, n]) => n > 1).map(([nhan]) => nhan));
  }, [dangTim, laHienRa, nhanMuc]);

  const xoaTim = useCallback(() => {
    setTuKhoa('');
    setViTriTro(-1);
  }, []);

  const doiTuKhoa = useCallback((v: string) => {
    setTuKhoa(v);
    // Gõ lại thì con trỏ về đầu: kết quả đầu tiên gần như luôn là cái người
    // dùng muốn, nên Enter ngay sau khi gõ phải mở được nó.
    setViTriTro(v.trim() ? 0 : -1);
  }, []);

  const xuLyPhim = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        // Còn chữ thì Esc xoá chữ; đã trắng rồi thì Esc nhả tiêu điểm, để
        // người dùng không bị kẹt trong ô tìm.
        if (tuKhoa) xoaTim();
        else oTim.current?.blur();
        return;
      }

      if (!dangTim || laHienRa.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setViTriTro((v) => (v + 1) % laHienRa.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setViTriTro((v) => (v <= 0 ? laHienRa.length - 1 : v - 1));
      } else if (e.key === 'Enter') {
        const dich = laHienRa[troHieuLuc >= 0 ? troHieuLuc : 0];
        if (!dich?.href) return;
        e.preventDefault();
        navigate(dich.href);
        // Mở xong thì xoá từ khoá: giữ lại thì cây vẫn bị lọc trong lúc người
        // dùng đã sang màn khác, trông như thanh điều hướng bị mất mục.
        xoaTim();
      }
    },
    [dangTim, laHienRa, navigate, troHieuLuc, tuKhoa, xoaTim],
  );

  /**
   * Nhóm nào mở: **mặc định chỉ mở nhóm chứa route đang xem**.
   *
   * Trước đây mọi nhóm đều mở, nên cây cao hơn 2000px và người dùng phải cuộn
   * qua một bức tường chữ để tìm một mục. Gập sẵn những nhóm không liên quan
   * thì cả 12 nhóm vừa trong một màn hình, mà vẫn đọc được tên nhóm.
   *
   * Thứ tự quyết định: đang tìm → mở hết (người dùng cần thấy kết quả, không
   * phải mở từng nhóm); người dùng tự mở → mở; tự đóng → đóng; còn lại → chỉ
   * mở nhóm đang chứa route hiện tại.
   */
  const nhomDangMo = useCallback(
    (section: NavSection): boolean => {
      if (dangTim) return true;
      if (nhomMo.has(section.khoaNhom)) return true;
      if (nhomDong.has(section.khoaNhom)) return false;
      return nhomChuaRoute(section, pathname);
    },
    [dangTim, nhomDong, nhomMo, pathname],
  );

  /* ------------------------------------------------------------------ *
   * Mở hết / gập hết.
   * ------------------------------------------------------------------ */

  /**
   * **Một** nút, hành động đổi theo trạng thái — không phải hai nút cạnh nhau.
   *
   * VS Code cố ý chỉ có một hành động ở tiêu đề (`Collapse Folders in
   * Explorer`); yêu cầu thêm nút mở hết (microsoft/vscode#88682) bị đóng
   * "not planned". Hai lý do họ nêu **không** áp vào đây — cây này cố định 12
   * nhóm, không phải hệ tệp, và không đệ quy vô hạn — nhưng bài học về giao
   * diện thì áp: nhồi hai nút vào đầu thanh điều hướng là thêm nhiễu đúng chỗ
   * đang chật.
   *
   * Nút đổi **hành động** chứ không đổi nghĩa: còn nhóm nào mở thì nó gập hết;
   * mọi nhóm đã gập thì nó mở hết. Nhãn và `title` luôn nói việc **sắp xảy
   * ra**, nên không bao giờ có cảnh người dùng phải đoán biểu tượng đang tả
   * trạng thái hay tả hành động.
   */
  const tenNhomHienCo = useMemo(() => sections.map((x) => x.khoaNhom), [sections]);

  const khoaChaHienCo = useMemo(
    () =>
      sections.flatMap((section) =>
        section.items.filter((i) => i.children).map((i) => khoaCha(section, i)),
      ),
    [sections],
  );

  /** Có nhóm nào đang mở không — quyết định nút làm gì khi bấm. */
  const conNhomMo = useMemo(
    () => sections.some((section) => nhomDangMo(section)),
    [nhomDangMo, sections],
  );

  const bamTatCa = useCallback(() => {
    datTatCa(!conNhomMo, tenNhomHienCo, khoaChaHienCo);
  }, [conNhomMo, datTatCa, khoaChaHienCo, tenNhomHienCo]);

  const fullName = user?.fullName?.trim();
  const avatarLetter = fullName ? fullName[0]!.toUpperCase() : '?';
  /*
   * Nhãn vai trò đến từ hai nguồn: `lib/auth/oidc.ts` trả **khoá** (`vaiTro.*`)
   * vì nó chạy ngoài cây React, còn `src/mock/` trả thẳng nhãn vì nó đóng vai
   * dữ liệu backend. Dịch khi là khoá, giữ nguyên khi không — nhờ vậy hai nguồn
   * sống cạnh nhau cho tới lúc API thật về và tầng mock bị xoá.
   */
  const vaiTroGoc = user?.roles[0];
  const firstRole = vaiTroGoc?.startsWith('vaiTro.')
    ? t(vaiTroGoc as Parameters<typeof t>[0])
    : vaiTroGoc;

  return (
    <>
    <aside
      className={cn(
        // Thanh điều hướng là một **thẻ nổi**, không phải panel sát viền: có
        // rãnh 8px ở trên, dưới và bên trái để thấy canvas quanh nó (§4.1).
        'app-sidebar relative my-2 ml-2 flex h-[calc(100vh-1rem)] shrink-0 select-none flex-col',
        'rounded-lg border border-default bg-surface text-content-secondary shadow-sm',
        'transition-[width] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] will-change-[width]',
        collapsed ? 'w-[76px]' : 'w-[264px]',
      )}
    >
      {/* Nút gập, nằm đè lên viền phải. */}
      <button
        type="button"
        onClick={toggleSidebar}
        title={collapsed ? t('chung.moRong') : t('chung.thuGon')}
        aria-label={collapsed ? t('khung.moThanhDieuHuong') : t('khung.gapThanhDieuHuong')}
        // `top-[22px]` đặt nút ngang hàng với logo; khi gập, logo rộng 48px
        // trong khung 76px nên hai thứ chạm nhau. Hạ xuống dưới hàng logo
        // (cao 64px) thì nút luôn nằm trên đường kẻ, không bao giờ đè lên ảnh.
        className="sidebar-edge-toggle absolute -right-3 top-[72px] z-20 flex h-6 w-6 items-center justify-center rounded-full border border-default bg-surface text-content-muted shadow-sm transition-colors hover:border-strong hover:text-primary-600"
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>

      {/* Khối logo. */}
      <div
        className={cn(
          'flex h-16 shrink-0 items-center justify-center',
          collapsed ? 'px-2' : 'px-5',
        )}
      >
        <Link to="/" className="min-w-0" aria-label={t('khung.veDashboard')}>
          {nhanDien.logoUrl ? (
            /*
             * Logo của tổ chức (ADR-UI-0016): ảnh màu nguyên bản nên **không**
             * gắn lớp `logo-native` — lớp đó ép logo trắng của Native thành đen
             * và sẽ làm hỏng logo của tổ chức. Tên tổ chức là dữ liệu nghiệp vụ,
             * không dịch.
             */
            <img
              src={nhanDien.logoUrl}
              alt={nhanDien.ten ?? ''}
              className={cn('h-auto max-h-10 object-contain', collapsed ? 'w-12' : 'w-32')}
            />
          ) : collapsed && nhanDien.tenVietTat ? (
            // Tổ chức chưa có logo nhưng đã đặt tên viết tắt: ở rail hẹp, tên đó nhận ra được hơn logo Native thu nhỏ.
            <span className="block max-w-[60px] truncate text-sm font-semibold text-primary-600">
              {nhanDien.tenVietTat}
            </span>
          ) : (
            <img
              src="/img/logo-native2.png"
              alt="FOXAI Native"
              width={collapsed ? 48 : 128}
              height={collapsed ? 14 : 37}
              // Màu logo theo chủ đề nằm ở lớp `logo-native` (styles/globals.css).
              //
              // Bề ngang phải đặt bằng **class CSS**, không chỉ bằng thuộc tính
              // `width` của thẻ: `object-contain` không giới hạn hộp CSS, nên nếu
              // thiếu class thì ảnh vẽ theo kích thước gốc (256px) và tràn ra
              // ngoài thanh điều hướng, đè lên cả nút gập.
              className={cn(
                'logo-native h-auto object-contain',
                collapsed ? 'w-12' : 'w-32',
              )}
            />
          )}
        </Link>
      </div>

      {/* Vùng điều hướng. Ẩn riêng thanh cuộn ở đây. */}
      {/*
        Vùng điều hướng.

        Bản gốc ẩn hẳn thanh cuộn ở đây (`scrollbarWidth: 'none'`). Ta **không**
        ẩn: cây điều hướng cao hơn màn hình ở phần lớn độ phân giải, mà nội dung
        bị cắt không có dấu hiệu gì thì coi như không tồn tại. Thanh cuộn mảnh
        6px của hệ (khai trong `globals.css`) vừa đủ làm manh mối mà không ồn.
      */}
      {/*
        Ô tìm chỉ hiện khi thanh mở rộng. Ở rail 76px không có chỗ cho một ô
        nhập dùng được, nên thay bằng nút kính lúp: bấm vào thì mở thanh ra và
        nhảy luôn vào ô tìm (`moOTim`).
      */}
      {collapsed ? (
        <div className="px-1.5 pb-1 pt-1">
          <button
            type="button"
            onClick={moOTim}
            title={t('khung.timManHinhPhimTat')}
            aria-label={t('khung.timManHinh')}
            className="flex w-full items-center justify-center rounded-lg py-2 text-content-muted transition-colors hover:bg-subtle hover:text-content-primary"
          >
            <Search size={18} />
          </button>
        </div>
      ) : (
        /*
          Ô tìm và nút mở/gập hết nằm **cùng một hàng**.
          Xếp thành hai hàng thì đầu thanh điều hướng có logo + ô tìm + hàng
          nút + tiêu đề nhóm đầu tiên, tức bốn tầng chữ trước khi tới mục đầu
          tiên — đúng cái "quá nhiều thông tin tại một chỗ" cần tránh. Nút là
          biểu tượng vuông 32px, chiếm chỗ của một ký tự trong ô tìm.
        */
        <div className="flex items-center gap-1.5 px-3 pb-1 pt-1">
          <SidebarSearch
            ref={oTim}
            giaTri={tuKhoa}
            onDoi={doiTuKhoa}
            soKetQua={laHienRa.length}
            onXuongPhim={xuLyPhim}
          />
          {/*
            Đang tìm thì **ẩn** nút: lúc đó mọi nhóm bị buộc mở để hiện kết
            quả, nên bấm "gập hết" không có tác dụng thấy được — một nút bấm
            vào không thấy gì xảy ra là tín hiệu sai. Xoá từ khoá là nút về.
          */}
          {!dangTim && (
            <button
              type="button"
              onClick={bamTatCa}
              // `title` và `aria-label` nói việc **sắp xảy ra**, không tả
              // trạng thái hiện tại.
              title={conNhomMo ? t('khung.gapHetCacNhom') : t('khung.moHetCacNhom')}
              aria-label={conNhomMo ? t('khung.gapHetCacNhom') : t('khung.moHetCacNhom')}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-default text-content-muted transition-colors hover:border-strong hover:text-content-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-100"
            >
              {conNhomMo ? <ChevronsDownUp size={14} /> : <ChevronsUpDown size={14} />}
            </button>
          )}
        </div>
      )}

      {/*
        ⚠️ Cố ý **không** gắn `role="listbox"` ở đây dù ô tìm là một combobox.
        Listbox chỉ được chứa `option`, mà vùng này còn có nút tiêu đề nhóm và
        nút mục cha — trình đọc màn hình gặp listbox có nút bên trong thì đếm
        sai số mục hoặc bỏ qua hẳn. Và `role="option"` trên một `<a>` **xoá**
        ngữ nghĩa liên kết: người dùng mất thông báo "liên kết" cùng các phím
        tắt nhảy theo liên kết.
        Thay vào đó giữ đúng `<nav>` với danh sách liên kết thật, rồi báo số
        kết quả qua vùng `aria-live` trong `SidebarSearch`. Mất `↑↓` theo chuẩn
        ARIA combobox, nhưng phím vẫn chạy cho người sáng, và người dùng trình
        đọc màn hình vẫn Tab qua liên kết như một thanh điều hướng bình thường.
      */}
      <nav
        aria-label={t('khung.dieuHuongChinh')}
        id="sidebar-ket-qua"
        className="min-h-0 flex-1 overflow-y-auto py-2"
      >
        {/*
          Rỗng **vì bộ lọc**: bỏ cả mô tả và nút hành động. Mời "tạo cái đầu
          tiên" khi người dùng vừa gõ sai một chữ là sai (§10.1).
        */}
        {dangTim && sectionsHienRa.length === 0 && (
          <div className="flex flex-col items-center px-4 py-8 text-center">
            <SearchX aria-hidden size={28} className="mb-2 text-content-muted" />
            <p className="text-sm font-medium text-content-primary">
              {t('khung.khongCoManHinhKhop')}
            </p>
          </div>
        )}

        {sectionsHienRa.map((section, sectionIndex) => {
          const tenNhomHien = tenNhom(section, t);
          const sectionOpen = nhomDangMo(section);

          return (
            <div key={section.khoaNhom}>
              {collapsed ? (
                // Khi gập, tiêu đề mục được thay bằng một đường kẻ — trừ nhóm
                // đầu tiên: ở đó đường kẻ nằm ngay dưới logo nên chỉ là nhiễu,
                // và còn trùng chỗ với nút gập.
                sectionIndex > 0 && <div className="mx-3 my-2 border-t border-default" />
              ) : (
                <button
                  type="button"
                  onClick={() => datNhom(section.khoaNhom, !sectionOpen)}
                  aria-expanded={sectionOpen}
                  className="sidebar-reveal group flex w-full items-center gap-1.5 px-5 pb-2 pt-4"
                >
                  {/* Tiêu đề mục mang **màu nhấn**, không phải màu xám: tiêu đề
                      và các mục con đọc như một khối cùng màu. */}
                  <span
                    className={cn(
                      'flex-1 text-left text-xs font-bold uppercase tracking-[0.08em] transition-colors',
                      SECTION_TONE,
                    )}
                  >
                    {tenNhomHien}
                  </span>
                  {/*
                    Nhóm đã gập **mà đang chứa màn đang xem** thì phải có dấu:
                    sau khi bấm "gập hết", người dùng ở một màn mà cả thanh
                    điều hướng không còn gì chỉ ra họ đang ở đâu — mất hẳn cảm
                    giác vị trí. Một chấm màu nhấn là đủ, và nó chỉ xuất hiện
                    đúng lúc viên thuốc bị ẩn.
                  */}
                  {!sectionOpen && nhomChuaRoute(section, pathname) && (
                    <>
                      <span
                        aria-hidden
                        className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-600"
                      />
                      {/* Chấm màu một mình không nói gì với trình đọc màn
                          hình — cần chữ tương đương (§15). */}
                      <span className="sr-only">{t('khung.chuaManDangXem')}</span>
                    </>
                  )}
                  <ChevronDown
                    size={14}
                    className={cn(
                      'shrink-0 opacity-50 transition-all duration-200 group-hover:opacity-100',
                      SECTION_TONE,
                      sectionOpen && 'rotate-180',
                    )}
                  />
                </button>
              )}

              {/*
                Gập theo chiều cao bằng mẹo grid, không đo bằng JS.
                Phần đã gập nhận thuộc tính `inert` để liên kết bên trong rời
                khỏi thứ tự tab (WCAG 2.4.3) — bỏ bước này là một lỗi điều hướng
                bằng bàn phím mà không ai thấy (§18.3 mục 8).
              */}
              <div
                className={cn(
                  'grid transition-all duration-200 ease-out',
                  sectionOpen || collapsed ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
              >
                <div className="overflow-hidden" inert={!sectionOpen && !collapsed}>
                  {section.items.map((item) => {
                    if (!item.children) {
                      return (
                        <SidebarLeafItem
                          key={item.href ?? item.khoa}
                          label={nhanMuc(item)}
                          href={item.href!}
                          icon={item.icon}
                          collapsed={collapsed}
                          matchPrefix={item.matchPrefix}
                          tuKhoa={tuDaTach}
                          dangTro={laDangTro(item)}
                          refMuc={laDangTro(item) ? mucDangTro : undefined}
                          nhomCha={nhanTrung.has(nhanMuc(item)) ? tenNhomHien : undefined}
                        />
                      );
                    }

                    const hasActiveChild = isChildActive(item, pathname);

                    /*
                     * Thanh đang gập: cụm con mở **ngang** qua `SidebarFlyout`.
                     * Không mở xuống dưới vì cả cây có ~59 mục, xếp dọc thành
                     * biểu tượng thì tràn khỏi màn hình; và biểu tượng con
                     * không nhãn trông y hệt biểu tượng cha. Mở ngang giữ được
                     * nhãn chữ mà không tốn chiều cao.
                     */
                    if (collapsed) {
                      return (
                        <SidebarFlyout
                          key={idMuc(item)}
                          label={nhanMuc(item)}
                          icon={item.icon}
                          muc={item.children}
                          hasActiveChild={hasActiveChild}
                        />
                      );
                    }

                    /*
                     * Mục cha mở khi: đang tìm (phải thấy kết quả), hoặc một
                     * mục con là route đang xem, hoặc người dùng chưa tự đóng.
                     */
                    const khoa = khoaCha(section, item);
                    const parentOpen = dangTim || hasActiveChild || !chaDong.has(khoa);

                    return (
                      <div key={idMuc(item)}>
                        <SidebarParentItem
                          label={nhanMuc(item)}
                          icon={item.icon}
                          collapsed={collapsed}
                          open={parentOpen}
                          hasActiveChild={hasActiveChild}
                          onToggle={() => datCha(khoa, !parentOpen)}
                          tuKhoa={tuDaTach}
                        />
                        <div
                          className={cn(
                            'grid transition-all duration-200 ease-out',
                            parentOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                          )}
                        >
                          <div className="overflow-hidden" inert={!parentOpen}>
                            {item.children.map((child) => (
                              <SidebarLeafItem
                                key={child.href ?? child.khoa}
                                label={nhanMuc(child)}
                                href={child.href!}
                                icon={child.icon}
                                collapsed={collapsed}
                                isChild
                                matchPrefix={child.matchPrefix}
                                tuKhoa={tuDaTach}
                                dangTro={laDangTro(child)}
                                refMuc={laDangTro(child) ? mucDangTro : undefined}
                                nhomCha={
                                  nhanTrung.has(nhanMuc(child)) ? tenNhomHien : undefined
                                }
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      {/* Dòng phiên bản. */}
      <div
        title={NHAN_CHI_TIET}
        className={cn(
          'shrink-0 border-t border-default px-3 pb-1 pt-2 text-2xs leading-none text-content-muted',
          collapsed && 'text-center',
        )}
      >
        {NHAN_PHIEN_BAN}
      </div>

      {/* Gói license và số ngày còn lại, ngay trên khối người dùng. Bản SaaS không dựng gì. */}
      <TheLicenseThanhBen collapsed={collapsed} />

      {/* Khối người dùng. */}
      <div
        className={cn(
          'flex shrink-0 items-center px-3 pb-3 pt-1.5',
          collapsed ? 'flex-col gap-2' : 'gap-2.5',
        )}
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-default bg-subtle text-xs font-bold text-content-secondary">
          {avatarLetter}
        </div>

        {!collapsed && (
          <div className="sidebar-reveal min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-tight text-content-primary">
              {fullName || t('khung.nguoiDung')}
            </p>
            <p className="mt-0.5 truncate text-2xs text-content-muted">
              {firstRole || t('khung.chuaXacDinh')}
            </p>
          </div>
        )}

        <MenuTaiKhoan
          collapsed={collapsed}
          tenDayDu={fullName ?? ''}
        />
      </div>
    </aside>

    </>
  );
}
